import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  UserRound, Shield, Smartphone, MapPin, Save, Loader2, LogOut,
  ChevronLeft, CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { sealSensitiveDoc } from '../utils/security';

import { validateSLPhone, sanitizePhone } from '../utils/validation';

export default function ManageProfile() {
  const { currentUser, userData, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    personal: {
      fullName: '',
      nicNumber: '',
      dob: '',
      gender: '',
      currentAddress: '',
      permanentAddress: '',
      mobileNumber: '',
      whatsappNumber: '',
      maritalStatus: 'Single',
    },
    financial: { dependents: '' }
  });

  useEffect(() => {
    if (userData?.personal) {
      setFormData(prev => ({
        ...prev,
        personal: { ...prev.personal, ...userData.personal },
        financial: { dependents: userData.financial?.dependents || '' }
      }));
    }
  }, [userData]);

  const set = (section, field, value) => {
    let finalValue = value;
    if (field === 'mobileNumber' || field === 'whatsappNumber') {
      finalValue = sanitizePhone(value);
    }
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [field]: finalValue } }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateSLPhone(formData.personal.mobileNumber)) {
      return setError('Please enter a valid Sri Lankan mobile number.');
    }
    if (formData.personal.whatsappNumber && !validateSLPhone(formData.personal.whatsappNumber)) {
      return setError('Please enter a valid Sri Lankan WhatsApp number.');
    }

    try {
      setLoading(true);
      await setDoc(doc(db, 'users', currentUser.uid), sealSensitiveDoc({
        personal: formData.personal,
        financial: { dependents: formData.financial.dependents }
      }), { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ icon: Icon, title, color = 'text-primary-600', bg = 'bg-primary-50' }) => (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-8 h-8 ${bg} ${color} rounded-lg flex items-center justify-center`}>
        <Icon size={16} />
      </div>
      <h3 className="text-sm font-bold text-stripe-dark uppercase tracking-wider">{title}</h3>
    </div>
  );

  return (
    <div className="min-h-screen bg-stripe-bg pb-24">
      {/* Header */}
      <header className="page-header">
        <div className="page-header-content">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="btn-icon">
              <ChevronLeft size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-stripe-dark">User Profile</h1>
              <p className="text-xs text-stripe-light font-medium hidden sm:block">Personal & contact information</p>
            </div>
          </div>
          <button onClick={logout} className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all" title="Logout">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-3 text-red-600 text-sm font-semibold animate-in slide-in-from-top-2 duration-300">
             <Shield size={18} className="shrink-0" />
             {error}
          </div>
        )}
        <form onSubmit={handleSave} className="space-y-6">

          {/* Core Credentials */}
          <div className="s-card">
            <SectionTitle icon={Shield} title="Core credentials" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="s-label">Full legal name</label>
                <input type="text" className="s-input"
                  value={formData.personal.fullName}
                  onChange={(e) => set('personal', 'fullName', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="s-label">NIC number</label>
                <div className="s-input bg-stripe-bg text-stripe-slate font-mono tracking-wider cursor-not-allowed">
                  {formData.personal.nicNumber || <span className="text-stripe-light">Not set</span>}
                </div>
              </div>
              <div>
                <label className="s-label">Date of birth</label>
                <input type="date" className="s-input"
                  value={formData.personal.dob}
                  onChange={(e) => set('personal', 'dob', e.target.value)}
                />
              </div>
              <div>
                <label className="s-label">Gender</label>
                <select className="s-select"
                  value={formData.personal.gender}
                  onChange={(e) => set('personal', 'gender', e.target.value)}>
                  <option value="">Select…</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Communication */}
          <div className="s-card">
            <SectionTitle icon={Smartphone} title="Contact details" color="text-blue-600" bg="bg-blue-50" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="s-label">Mobile number</label>
                <input type="tel" className="s-input"
                  placeholder="07X XXX XXXX"
                  value={formData.personal.mobileNumber}
                  onChange={(e) => set('personal', 'mobileNumber', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="s-label">WhatsApp number</label>
                <input type="tel" className="s-input"
                  placeholder="07X XXX XXXX"
                  value={formData.personal.whatsappNumber}
                  onChange={(e) => set('personal', 'whatsappNumber', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="s-card">
            <SectionTitle icon={MapPin} title="Addresses" color="text-emerald-600" bg="bg-emerald-50" />
            <div className="space-y-4">
              <div>
                <label className="s-label">Current address</label>
                <textarea className="s-textarea min-h-[80px]"
                  placeholder="No, Street, City"
                  value={formData.personal.currentAddress}
                  onChange={(e) => set('personal', 'currentAddress', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="s-label">Permanent address</label>
                <textarea className="s-textarea min-h-[80px]"
                  placeholder="No, Street, City"
                  value={formData.personal.permanentAddress}
                  onChange={(e) => set('personal', 'permanentAddress', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Family */}
          <div className="s-card">
            <SectionTitle icon={UserRound} title="Family status" color="text-purple-600" bg="bg-purple-50" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="s-label">Marital status</label>
                <select className="s-select"
                  value={formData.personal.maritalStatus}
                  onChange={(e) => set('personal', 'maritalStatus', e.target.value)}>
                  <option>Single</option>
                  <option>Married</option>
                  <option>Divorced</option>
                  <option>Widowed</option>
                </select>
              </div>
              <div>
                <label className="s-label">Dependents</label>
                <input type="number" min="0" className="s-input"
                  placeholder="0"
                  value={formData.financial.dependents}
                  onChange={(e) => set('financial', 'dependents', e.target.value)}
                />
              </div>
            </div>
          </div>


          {/* Save */}
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-stripe-slate">
              Changes will update your eligibility across the network.
            </p>
            <button
              type="submit"
              disabled={loading}
              className={`btn-primary gap-2 min-w-[140px] disabled:opacity-50 ${saved ? '!bg-green-600' : ''}`}
            >
              {loading ? (
                <><Loader2 size={15} className="animate-spin" /> Saving…</>
              ) : saved ? (
                <><CheckCircle2 size={15} /> Saved!</>
              ) : (
                <><Save size={15} /> Save profile</>
              )}
            </button>
          </div>
        </form>
      </main>

      <BottomNav />
    </div>
  );
}
